import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  getAdminThread,
  getAdminThreads,
  getAllOrganizations,
  getAllUsers,
  triggerAdminConversationRestitch,
  triggerAdminConversationRetag,
  triggerAdminThreadReanalyze,
} from "../../services/adminService";
import { useAdminData } from "../../hooks/useAdminData";
import { usePersistentState } from "../../hooks/usePersistentState";
import { createToast } from "../../utils/toastUtils";
import { formatDate } from "../../utils/dateUtils";
import Pagination from "../shared/Pagination";
import CoachingReportView from "../CoachingReportView";

const ITEMS_PER_PAGE = 5;

const STATUS_COLORS = {
  READY: "green",
  PROCESSING: "orange",
  TRANSCRIBING: "blue",
  RECORDING: "red",
  IDLE: "gray",
  ENDED: "green",
  ACTIVE: "red",
  ABANDONED: "gray",
  OK: "green",
  ERROR: "red",
  committed: "green",
  queued: "orange",
  running: "blue",
  failed: "red",
  superseded: "gray",
};

const OBJECT_COLORS = {
  thread: "blue",
  analysis: "purple",
  conversation: "green",
  fact: "cyan",
  wikiPage: "teal",
  artifact: "pink",
  batch: "orange",
  snippet: "yellow",
  detailItem: "gray",
  event: "blue",
  checkpoint: "purple",
  pendingWork: "orange",
  deadLetter: "red",
};

const displayDate = (value) => (value ? formatDate(value) : "-");
const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

const shortText = (value, max = 96) => {
  const text = displayValue(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

const objectId = (type, data) => `${type}:${data?.id ?? data?.key ?? data?.processor_key ?? data?.slug ?? ""}`;

const getAnalysisJson = (run) =>
  run?.analysis_json ?? run?.analysis ?? run?.result ?? null;

const Section = ({ title, count, children, action }) => (
  <Box borderTopWidth="1px" borderTopColor="gray.100" pt={4}>
    <Flex align="center" justify="space-between" gap={3} mb={3}>
      <HStack spacing={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
          {title}
        </Text>
        {count !== undefined && (
          <Badge variant="subtle" colorScheme="gray" borderRadius="full">
            {count}
          </Badge>
        )}
      </HStack>
      {action}
    </Flex>
    {children}
  </Box>
);

const MetaRow = ({ label, value, mono = false }) => (
  <Flex justify="space-between" align="baseline" gap={3} py={1}>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Text
      fontSize="xs"
      color="gray.800"
      fontFamily={mono ? "mono" : "inherit"}
      textAlign="right"
      noOfLines={1}
    >
      {displayValue(value)}
    </Text>
  </Flex>
);

const ObjectButton = ({ type, data, label, sublabel, badge, selected, onClick, action }) => (
  <Flex
    role="button"
    tabIndex={0}
    textAlign="left"
    align="center"
    justify="space-between"
    gap={3}
    width="100%"
    px={3}
    py={2.5}
    borderWidth="1px"
    borderColor={selected ? `${OBJECT_COLORS[type] || "blue"}.300` : "gray.200"}
    bg={selected ? `${OBJECT_COLORS[type] || "blue"}.50` : "white"}
    borderRadius="md"
    _hover={{ bg: selected ? `${OBJECT_COLORS[type] || "blue"}.50` : "gray.50" }}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.(event);
      }
    }}
  >
    <VStack align="start" spacing={0} minW={0} flex="1">
      <HStack spacing={2} minW={0}>
        <Badge colorScheme={OBJECT_COLORS[type] || "gray"} variant="subtle" borderRadius="full">
          {type}
        </Badge>
        {badge}
      </HStack>
      <Text fontSize="sm" fontWeight="semibold" color="gray.900" noOfLines={1}>
        {label || `${type} ${data?.id ?? ""}`}
      </Text>
      {sublabel && (
        <Text fontSize="xs" color="gray.500" noOfLines={1}>
          {sublabel}
        </Text>
      )}
    </VStack>
    {action}
  </Flex>
);

const EmptyState = ({ children }) => (
  <Box py={5} px={3} borderWidth="1px" borderStyle="dashed" borderColor="gray.200" borderRadius="md">
    <Text fontSize="sm" color="gray.400">{children}</Text>
  </Box>
);

const JsonTree = ({ value, name = "root", defaultOpen = true, depth = 0 }) => {
  const [open, setOpen] = useState(defaultOpen || depth < 1);
  const isArray = Array.isArray(value);
  const isObject = value && typeof value === "object" && !isArray;

  if (!isArray && !isObject) {
    return (
      <HStack align="baseline" spacing={2} pl={depth * 3}>
        <Text fontSize="xs" color="gray.500" fontFamily="mono">{name}:</Text>
        <Code fontSize="xs" whiteSpace="pre-wrap" colorScheme="gray">
          {JSON.stringify(value)}
        </Code>
      </HStack>
    );
  }

  const entries = isArray
    ? value.map((item, idx) => [idx, item])
    : Object.entries(value);
  const shapeLabel = isArray ? `Array(${entries.length})` : `Object(${entries.length})`;

  return (
    <Box pl={depth * 3}>
      <HStack
        as="button"
        type="button"
        spacing={2}
        onClick={() => setOpen((v) => !v)}
        py={1}
        textAlign="left"
      >
        <Text fontSize="xs" fontFamily="mono" color="gray.700">
          {open ? "v" : ">"} {name}
        </Text>
        <Text fontSize="xs" color="gray.400">{shapeLabel}</Text>
      </HStack>
      {open && (
        <VStack align="stretch" spacing={1}>
          {entries.map(([key, child]) => (
            <JsonTree
              key={`${depth}-${key}`}
              name={String(key)}
              value={child}
              defaultOpen={depth < 1}
              depth={depth + 1}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};

const JsonPanel = ({ value }) => (
  <Box
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="md"
    bg="gray.50"
    p={3}
    overflow="auto"
    maxH="720px"
  >
    <JsonTree value={value ?? {}} />
  </Box>
);

const ThreadList = ({
  threads,
  selectedThreadId,
  onSelect,
  skip,
  totalCount,
  currentPage,
  setCurrentPage,
  threadsLoading,
}) => (
  <Box borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" overflow="hidden">
    <Box px={4} py={3} borderBottomWidth="1px" borderBottomColor="gray.100">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
        Threads
      </Text>
      <Text fontSize="xs" color="gray.400">5 per page, inbox first</Text>
    </Box>
    <VStack align="stretch" spacing={0}>
      {threads.map((thread, idx) => (
        <Box
          key={thread.id}
          as="button"
          type="button"
          textAlign="left"
          px={4}
          py={3}
          borderBottomWidth={idx < threads.length - 1 ? "1px" : "0"}
          borderBottomColor="gray.100"
          bg={String(selectedThreadId) === String(thread.id) ? "blue.50" : "white"}
          _hover={{ bg: "gray.50" }}
          onClick={() => onSelect(thread.id)}
        >
          <Flex justify="space-between" align="start" gap={3}>
            <VStack align="start" spacing={1} minW={0}>
              <HStack spacing={2}>
                {thread.is_inbox && (
                  <Badge colorScheme="blue" borderRadius="full" variant="subtle">Mailbox</Badge>
                )}
                <Badge colorScheme={STATUS_COLORS[thread.status] || "gray"} borderRadius="full" variant="subtle">
                  {thread.status || "IDLE"}
                </Badge>
              </HStack>
              <Text fontSize="sm" fontWeight="bold" color="gray.900" noOfLines={1}>
                {thread.title || `Thread ${thread.id}`}
              </Text>
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                #{thread.id} | user {thread.user_id} | {thread.recording_count ?? 0} recordings
              </Text>
              <Text fontSize="xs" color="gray.400">
                Last recorded {displayDate(thread.last_recorded_at)}
              </Text>
            </VStack>
          </Flex>
        </Box>
      ))}
      {threads.length === 0 && (
        <Box py={12} textAlign="center">
          <Text color="gray.400" fontSize="sm">No threads found</Text>
        </Box>
      )}
    </VStack>
    <Flex justify="space-between" align="center" px={4} py={3} borderTopWidth="1px" borderTopColor="gray.100">
      <Text fontSize="xs" color="gray.400">
        {threads.length === 0 ? 0 : skip + 1}-{Math.min(skip + ITEMS_PER_PAGE, totalCount)} of {totalCount}
      </Text>
      <Pagination
        currentPage={currentPage}
        totalItems={totalCount}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
        isDisabled={threadsLoading}
      />
    </Flex>
  </Box>
);

const SummaryPanel = ({
  thread,
  selectedObject,
  onSelectObject,
  onReanalyze,
  onRetag,
  onRestitch,
  actionKey,
}) => {
  if (!thread) {
    return (
      <Box borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" minH="260px">
        <Flex align="center" justify="center" py={16}>
          <Text fontSize="sm" color="gray.400">Select a thread to inspect</Text>
        </Flex>
      </Box>
    );
  }

  const isSelected = (type, data) =>
    objectId(selectedObject?.type, selectedObject?.data) === objectId(type, data);
  const analyses = thread.analysis_runs || [];
  const conversations = thread.conversations || [];
  const wikiPages = thread.wiki_pages || [];
  const facts = thread.facts || [];
  const artifacts = thread.thread_artifacts || [];
  const internal = thread.internal_state || {};

  return (
    <Box borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" p={4}>
      <Flex justify="space-between" gap={4} align="start" mb={4}>
        <VStack align="start" spacing={1} minW={0}>
          <HStack spacing={2}>
            {thread.is_inbox && <Badge colorScheme="blue" borderRadius="full">Mailbox</Badge>}
            <Badge colorScheme={STATUS_COLORS[thread.status] || "gray"} borderRadius="full">
              {thread.status || "IDLE"}
            </Badge>
          </HStack>
          <Text fontSize="lg" fontWeight="bold" color="gray.900" noOfLines={1}>
            {thread.title || `Thread ${thread.id}`}
          </Text>
          <Text fontSize="xs" color="gray.500" fontFamily="mono">thread_id={thread.id}</Text>
        </VStack>
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isLoading={actionKey === `reanalyze:${thread.id}`}
          onClick={onReanalyze}
        >
          Reanalyze
        </Button>
      </Flex>

      <Grid templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }} gap={4} mb={4}>
        <Box>
          <MetaRow label="User" value={thread.user_id} mono />
          <MetaRow label="Org" value={thread.organization_id} mono />
          <MetaRow label="Engine thread" value={thread.engine_thread_id} mono />
          <MetaRow label="Recordings" value={thread.recording_count ?? 0} />
        </Box>
        <Box>
          <MetaRow label="Created" value={displayDate(thread.created_at)} />
          <MetaRow label="Updated" value={displayDate(thread.updated_at)} />
          <MetaRow label="Last recorded" value={displayDate(thread.last_recorded_at)} />
          <MetaRow label="Last analyzed" value={displayDate(thread.last_analyzed_at)} />
        </Box>
      </Grid>

      <ObjectButton
        type="thread"
        data={thread}
        label={`Thread ${thread.id}`}
        sublabel="Full thread and internal state"
        selected={isSelected("thread", thread)}
        onClick={() => onSelectObject("thread", thread)}
      />

      <VStack align="stretch" spacing={4} mt={4}>
        <Section title="Analysis" count={analyses.length}>
          {analyses.length === 0 ? (
            <EmptyState>No analysis runs yet</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2}>
              {analyses.map((run) => (
                <ObjectButton
                  key={run.id}
                  type="analysis"
                  data={run}
                  label={`${run.workflow_key || "analysis"} ${run.analysis_type || ""}`.trim()}
                  sublabel={`run ${run.id} | ${run.status} | ${displayDate(run.committed_at || run.created_at)}`}
                  selected={isSelected("analysis", run)}
                  onClick={() => onSelectObject("analysis", run)}
                  badge={run.is_active ? <Badge colorScheme="green" borderRadius="full">Active</Badge> : null}
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Conversations" count={conversations.length}>
          {conversations.length === 0 ? (
            <EmptyState>No conversations in this thread</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2}>
              {conversations.map((conv) => (
                <ObjectButton
                  key={conv.id}
                  type="conversation"
                  data={conv}
                  label={`Conversation ${conv.id}`}
                  sublabel={`${conv.recording_state || "UNKNOWN"} | ${conv.analysis_state || "NO_ANALYSIS"} | ${conv.audio_batch_count || 0} buffers`}
                  selected={isSelected("conversation", conv)}
                  onClick={() => onSelectObject("conversation", conv)}
                  badge={
                    <Badge colorScheme={STATUS_COLORS[conv.recording_state] || "gray"} borderRadius="full">
                      {conv.recording_state || "UNKNOWN"}
                    </Badge>
                  }
                  action={
                    <HStack spacing={1} onClick={(event) => event.stopPropagation()}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="purple"
                        isLoading={actionKey === `retag:${conv.id}`}
                        onClick={() => onRetag(conv.id)}
                      >
                        Retag
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="orange"
                        isLoading={actionKey === `restitch:${conv.id}`}
                        onClick={() => onRestitch(conv.id)}
                      >
                        Restitch
                      </Button>
                    </HStack>
                  }
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Wiki Pages" count={wikiPages.length}>
          {wikiPages.length === 0 ? (
            <EmptyState>No wiki page facts</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2}>
              {wikiPages.map((page) => (
                <ObjectButton
                  key={page.id}
                  type="wikiPage"
                  data={page}
                  label={page.category?.replace("wiki:page:", "") || `Wiki fact ${page.id}`}
                  sublabel={shortText(page.value)}
                  selected={isSelected("wikiPage", page)}
                  onClick={() => onSelectObject("wikiPage", page)}
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Current Facts" count={facts.length}>
          {facts.length === 0 ? (
            <EmptyState>No current facts</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2} maxH="360px" overflowY="auto" pr={1}>
              {facts.map((fact) => (
                <ObjectButton
                  key={fact.id}
                  type="fact"
                  data={fact}
                  label={fact.category}
                  sublabel={shortText(fact.value)}
                  selected={isSelected("fact", fact)}
                  onClick={() => onSelectObject("fact", fact)}
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Artifacts" count={artifacts.length}>
          {artifacts.length === 0 ? (
            <EmptyState>No artifacts</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2}>
              {artifacts.map((artifact) => (
                <ObjectButton
                  key={artifact.id}
                  type="artifact"
                  data={artifact}
                  label={artifact.key}
                  sublabel={`source conv ${artifact.source_conversation_id || "-"}`}
                  selected={isSelected("artifact", artifact)}
                  onClick={() => onSelectObject("artifact", artifact)}
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Internal Lanes">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            {(internal.workflow_checkpoints || []).map((checkpoint) => (
              <ObjectButton
                key={checkpoint.processor_key}
                type="checkpoint"
                data={checkpoint}
                label={checkpoint.processor_key}
                sublabel={`cursor ${checkpoint.cursor ?? "-"} | v${checkpoint.version}`}
                selected={isSelected("checkpoint", checkpoint)}
                onClick={() => onSelectObject("checkpoint", checkpoint)}
              />
            ))}
            {(internal.recent_events || []).slice(0, 6).map((event) => (
              <ObjectButton
                key={event.id}
                type="event"
                data={event}
                label={event.kind}
                sublabel={`event ${event.id} | ${displayDate(event.created_at)}`}
                selected={isSelected("event", event)}
                onClick={() => onSelectObject("event", event)}
              />
            ))}
            {(internal.pending_work || []).map((item) => (
              <ObjectButton
                key={item.id}
                type="pendingWork"
                data={item}
                label={item.type}
                sublabel={item.group_key || `pending ${item.id}`}
                selected={isSelected("pendingWork", item)}
                onClick={() => onSelectObject("pendingWork", item)}
              />
            ))}
            {(internal.dead_letters || []).map((item) => (
              <ObjectButton
                key={item.id}
                type="deadLetter"
                data={item}
                label={item.processor_key}
                sublabel={item.error || `event ${item.event_id}`}
                selected={isSelected("deadLetter", item)}
                onClick={() => onSelectObject("deadLetter", item)}
              />
            ))}
          </SimpleGrid>
        </Section>
      </VStack>
    </Box>
  );
};

const ThreadDetail = ({ thread }) => (
  <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
    <Box borderWidth="1px" borderRadius="md" borderColor="gray.200" p={4}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>Thread State</Text>
      <MetaRow label="ID" value={thread.id} mono />
      <MetaRow label="Mailbox" value={thread.is_inbox} />
      <MetaRow label="Engine thread" value={thread.engine_thread_id} mono />
      <MetaRow label="Archived" value={displayDate(thread.archived_at)} />
      <MetaRow label="Status" value={thread.status} />
    </Box>
    <Box borderWidth="1px" borderRadius="md" borderColor="gray.200" p={4}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>Counts</Text>
      <MetaRow label="Conversations" value={(thread.conversations || []).length} />
      <MetaRow label="Analysis runs" value={(thread.analysis_runs || []).length} />
      <MetaRow label="Current facts" value={(thread.facts || []).length} />
      <MetaRow label="Wiki pages" value={(thread.wiki_pages || []).length} />
    </Box>
    <Box borderWidth="1px" borderRadius="md" borderColor="gray.200" p={4}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>Internal</Text>
      <MetaRow label="Checkpoints" value={(thread.internal_state?.workflow_checkpoints || []).length} />
      <MetaRow label="Recent events" value={(thread.internal_state?.recent_events || []).length} />
      <MetaRow label="Pending work" value={(thread.internal_state?.pending_work || []).length} />
      <MetaRow label="Dead letters" value={(thread.internal_state?.dead_letters || []).length} />
    </Box>
  </SimpleGrid>
);

const AnalysisDetail = ({ run, threadId }) => {
  const analysis = getAnalysisJson(run);
  return (
    <Grid templateColumns={{ base: "1fr", xl: "minmax(0, 1.15fr) minmax(380px, 0.85fr)" }} gap={4}>
      <Box minW={0} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
        <Box px={4} py={3} borderBottomWidth="1px" borderBottomColor="gray.100" bg="gray.50">
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="bold">Rendered Analysis</Text>
            <Badge colorScheme={STATUS_COLORS[run.status] || "gray"}>{run.status}</Badge>
            {run.analysis_type && <Badge colorScheme="purple">{run.analysis_type}</Badge>}
          </HStack>
        </Box>
        <Box p={4} maxH="760px" overflowY="auto">
          {analysis ? (
            <CoachingReportView analysis={analysis} threadId={threadId} displayMode="embed" />
          ) : (
            <EmptyState>No analysis JSON available for this run</EmptyState>
          )}
        </Box>
      </Box>
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="bold" mb={3}>Analysis JSON</Text>
        <JsonPanel value={run} />
      </Box>
    </Grid>
  );
};

const ConversationDetail = ({ conversation, selectedObject, onSelectObject }) => {
  const batches = conversation.audio_batches || [];
  const details = conversation.detail_items || [];
  const isSelected = (type, data) =>
    objectId(selectedObject?.type, selectedObject?.data) === objectId(type, data);

  return (
    <Grid templateColumns={{ base: "1fr", xl: "360px minmax(0, 1fr)" }} gap={4}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Text fontSize="sm" fontWeight="bold" mb={3}>Conversation State</Text>
          <MetaRow label="ID" value={conversation.id} mono />
          <MetaRow label="Recording" value={conversation.recording_state} />
          <MetaRow label="Analysis" value={conversation.analysis_state} />
          <MetaRow label="Pipeline" value={conversation.pipeline_state} />
          <MetaRow label="Role" value={`${conversation.role_id || "-"} v${conversation.role_version || "-"}`} />
          <MetaRow label="Device" value={conversation.device_id} mono />
          <MetaRow label="Watermark" value={conversation.stream_ended_at_watermark_ms} />
        </Box>

        <Section title="Audio Buffers" count={batches.length}>
          {batches.length === 0 ? (
            <EmptyState>No audio buffers linked through audio_batches.conversation_id</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2}>
              {batches.map((batch) => (
                <ObjectButton
                  key={batch.id}
                  type="batch"
                  data={batch}
                  label={`Buffer ${shortText(batch.id, 18)}`}
                  sublabel={`${batch.state} | ${batch.snippets?.length || 0} snippets | ${Math.round((batch.total_duration_ms || 0) / 1000)}s`}
                  selected={isSelected("batch", batch)}
                  onClick={() => onSelectObject("batch", batch)}
                />
              ))}
            </VStack>
          )}
        </Section>

        <Section title="Detail Items" count={details.length}>
          {details.length === 0 ? (
            <EmptyState>No conversation detail items</EmptyState>
          ) : (
            <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto" pr={1}>
              {details.map((item) => (
                <ObjectButton
                  key={item.id}
                  type="detailItem"
                  data={item}
                  label={`${item.type} #${item.index}`}
                  sublabel={`item ${item.id} | ${displayDate(item.created_at)}`}
                  selected={isSelected("detailItem", item)}
                  onClick={() => onSelectObject("detailItem", item)}
                />
              ))}
            </VStack>
          )}
        </Section>
      </VStack>
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="bold" mb={3}>Conversation JSON</Text>
        <JsonPanel value={conversation} />
      </Box>
    </Grid>
  );
};

const BatchDetail = ({ batch, selectedObject, onSelectObject }) => {
  const snippets = batch.snippets || [];
  const isSelected = (type, data) =>
    objectId(selectedObject?.type, selectedObject?.data) === objectId(type, data);
  return (
    <Grid templateColumns={{ base: "1fr", xl: "380px minmax(0, 1fr)" }} gap={4}>
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
        <Text fontSize="sm" fontWeight="bold" mb={3}>Audio Buffer</Text>
        <MetaRow label="ID" value={batch.id} mono />
        <MetaRow label="State" value={batch.state} />
        <MetaRow label="Source" value={batch.source} />
        <MetaRow label="Encoding" value={batch.audio_encoding} />
        <MetaRow label="Chunks" value={`${batch.total_chunks} total / ${snippets.length} rows`} />
        <MetaRow label="Duration" value={`${Math.round((batch.total_duration_ms || 0) / 1000)}s`} />
        <Divider my={3} />
        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>Snippets</Text>
        {snippets.length === 0 ? (
          <EmptyState>No snippets loaded for this buffer</EmptyState>
        ) : (
          <VStack align="stretch" spacing={2} maxH="520px" overflowY="auto" pr={1}>
            {snippets.map((snippet) => (
              <ObjectButton
                key={snippet.id}
                type="snippet"
                data={snippet}
                label={`Snippet ${snippet.id}`}
                sublabel={`chunk ${snippet.chunk_index ?? "-"} | ${Math.round(((snippet.end_timestamp_ms || 0) - (snippet.start_timestamp_ms || 0)) / 1000)}s`}
                selected={isSelected("snippet", snippet)}
                onClick={() => onSelectObject("snippet", snippet)}
              />
            ))}
          </VStack>
        )}
      </Box>
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="bold" mb={3}>Buffer JSON</Text>
        <JsonPanel value={batch} />
      </Box>
    </Grid>
  );
};

const FactDetail = ({ fact, title = "Fact" }) => (
  <Grid templateColumns={{ base: "1fr", lg: "360px minmax(0, 1fr)" }} gap={4}>
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>{title}</Text>
      <MetaRow label="ID" value={fact.id} mono />
      <MetaRow label="Category" value={fact.category} />
      <MetaRow label="Source event" value={fact.source_event_id} mono />
      <MetaRow label="Supersedes" value={fact.supersedes_fact_id} mono />
      <MetaRow label="Embedding" value={fact.has_embedding ? "present" : "missing"} />
      <Divider my={3} />
      <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Value</Text>
      <Text fontSize="sm" whiteSpace="pre-wrap">{fact.value}</Text>
      {fact.context && (
        <>
          <Text fontSize="xs" color="gray.500" fontWeight="bold" mt={4} mb={1}>Context</Text>
          <Text fontSize="sm" whiteSpace="pre-wrap">{fact.context}</Text>
        </>
      )}
    </Box>
    <Box minW={0}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>Fact JSON</Text>
      <JsonPanel value={fact} />
    </Box>
  </Grid>
);

const GenericDetail = ({ object }) => (
  <Box>
    <Text fontSize="sm" fontWeight="bold" mb={3}>
      {object?.type || "Object"} Details
    </Text>
    <JsonPanel value={object?.data} />
  </Box>
);

const InspectorDetail = ({ thread, selectedObject, onSelectObject }) => {
  if (!selectedObject) return null;
  const { type, data } = selectedObject;

  return (
    <Box borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" p={4}>
      <Flex align="center" justify="space-between" gap={3} mb={4}>
        <HStack spacing={2}>
          <Badge colorScheme={OBJECT_COLORS[type] || "gray"} borderRadius="full">
            {type}
          </Badge>
          <Text fontSize="lg" fontWeight="bold" color="gray.900">
            {type === "thread" && `Thread ${data.id}`}
            {type === "analysis" && `Analysis ${data.id}`}
            {type === "conversation" && `Conversation ${data.id}`}
            {type === "batch" && `Audio Buffer ${shortText(data.id, 20)}`}
            {type === "snippet" && `Snippet ${data.id}`}
            {type === "detailItem" && `${data.type} ${data.index}`}
            {(type === "fact" || type === "wikiPage") && `${data.category || "Fact"} ${data.id}`}
            {!["thread", "analysis", "conversation", "batch", "snippet", "detailItem", "fact", "wikiPage"].includes(type) &&
              `${type} ${data?.id ?? data?.key ?? data?.processor_key ?? ""}`}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.400" fontFamily="mono">
          {objectId(type, data)}
        </Text>
      </Flex>

      {type === "thread" && <ThreadDetail thread={data} />}
      {type === "analysis" && <AnalysisDetail run={data} threadId={thread?.id} />}
      {type === "conversation" && (
        <ConversationDetail
          conversation={data}
          selectedObject={selectedObject}
          onSelectObject={onSelectObject}
        />
      )}
      {type === "batch" && (
        <BatchDetail
          batch={data}
          selectedObject={selectedObject}
          onSelectObject={onSelectObject}
        />
      )}
      {(type === "fact" || type === "wikiPage") && (
        <FactDetail fact={data} title={type === "wikiPage" ? "Wiki Page Fact" : "Fact"} />
      )}
      {["snippet", "detailItem", "artifact", "event", "checkpoint", "pendingWork", "deadLetter"].includes(type) && (
        <GenericDetail object={selectedObject} />
      )}
    </Box>
  );
};

const ThreadManagement = () => {
  const [filterOrgId, setFilterOrgId] = usePersistentState(
    "admin:threadManagement:filterOrgId",
    ""
  );
  const [filterUserId, setFilterUserId] = usePersistentState(
    "admin:threadManagement:filterUserId",
    ""
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionKey, setActionKey] = useState(null);
  const toast = useToast();
  const toastHelper = createToast(toast);

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;

  const fetchOrganizations = useCallback(() => getAllOrganizations(), []);
  const fetchUsers = useCallback(() => getAllUsers(filterOrgId || null), [filterOrgId]);
  const fetchThreads = useCallback(() => {
    const params = {
      skip,
      limit: ITEMS_PER_PAGE,
      archived: "false",
    };
    if (filterOrgId) params.organization_id = filterOrgId;
    if (filterUserId) params.user_id = filterUserId;
    return getAdminThreads(params);
  }, [filterOrgId, filterUserId, skip]);

  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError,
  } = useAdminData(fetchOrganizations, []);
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useAdminData(fetchUsers, [filterOrgId]);
  const {
    data: threadsData,
    loading: threadsLoading,
    error: threadsError,
    refetch: refetchThreads,
  } = useAdminData(fetchThreads, [filterOrgId, filterUserId, skip]);

  const organizations = organizationsData?.organizations || [];
  const users = usersData?.users || [];
  const threads = threadsData?.items || [];
  const totalCount = threadsData?.total_count || 0;
  const loading = orgsLoading || usersLoading || threadsLoading;
  const error = orgsError || usersError || threadsError;

  useEffect(() => {
    setSelectedThreadId(null);
    setSelectedThread(null);
    setSelectedObject(null);
  }, [filterOrgId, filterUserId]);

  const selectObject = (type, data) => {
    setSelectedObject({ type, data });
  };

  const loadThreadDetail = async (threadId) => {
    setSelectedThreadId(threadId);
    setDetailLoading(true);
    try {
      const detail = await getAdminThread(threadId);
      setSelectedThread(detail);
      setSelectedObject({ type: "thread", data: detail });
    } catch (err) {
      toastHelper.error(
        "Failed to load thread",
        err.response?.data?.detail || "Could not fetch thread detail"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOrgChange = (value) => {
    setFilterOrgId(value);
    setFilterUserId("");
    setCurrentPage(1);
  };

  const handleUserChange = (value) => {
    setFilterUserId(value);
    setCurrentPage(1);
  };

  const runThreadAction = async (label, key, fn) => {
    setActionKey(key);
    try {
      await fn();
      toastHelper.success(label);
      refetchThreads();
      if (selectedThreadId) {
        await loadThreadDetail(selectedThreadId);
      }
    } catch (err) {
      toastHelper.error(label, err.response?.data?.detail || "Action failed");
    } finally {
      setActionKey(null);
    }
  };

  const handleReanalyze = () => {
    if (!selectedThreadId) return;
    runThreadAction("Reanalysis triggered", `reanalyze:${selectedThreadId}`, () =>
      triggerAdminThreadReanalyze(selectedThreadId)
    );
  };

  const handleRetag = (conversationId) => {
    runThreadAction("Retag triggered", `retag:${conversationId}`, () =>
      triggerAdminConversationRetag(conversationId)
    );
  };

  const handleRestitch = (conversationId) => {
    if (!window.confirm(`Restitch conversation ${conversationId}? This deletes and rebuilds transcript detail items.`)) return;
    runThreadAction("Restitch triggered", `restitch:${conversationId}`, () =>
      triggerAdminConversationRestitch(conversationId)
    );
  };

  const detailContent = useMemo(() => {
    if (!selectedThreadId) return null;
    if (detailLoading) {
      return (
        <Flex align="center" justify="center" py={16}>
          <Spinner size="md" color="blue.500" />
        </Flex>
      );
    }
    return (
      <InspectorDetail
        thread={selectedThread}
        selectedObject={selectedObject}
        onSelectObject={selectObject}
      />
    );
  }, [detailLoading, selectedObject, selectedThread, selectedThreadId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error?.message || "Failed to load threads"}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      <Flex gap={4} wrap="wrap" align="flex-end">
        <FormControl maxW="280px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Organization
          </FormLabel>
          <Select
            size="sm"
            value={filterOrgId}
            onChange={(e) => handleOrgChange(e.target.value)}
            placeholder="All organizations"
            borderRadius="md"
            bg="white"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="340px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            User
          </FormLabel>
          <Select
            size="sm"
            value={filterUserId}
            onChange={(e) => handleUserChange(e.target.value)}
            placeholder="All users"
            borderRadius="md"
            bg="white"
          >
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.email}</option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      <Grid templateColumns={{ base: "1fr", xl: "380px minmax(0, 1fr)" }} gap={5} alignItems="start">
        <ThreadList
          threads={threads}
          selectedThreadId={selectedThreadId}
          onSelect={loadThreadDetail}
          skip={skip}
          totalCount={totalCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          threadsLoading={threadsLoading}
        />
        {detailLoading && !selectedThread ? (
          <Box borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" minH="260px">
            <Flex align="center" justify="center" py={16}>
              <Spinner size="md" color="blue.500" />
            </Flex>
          </Box>
        ) : (
          <SummaryPanel
            thread={selectedThread}
            selectedObject={selectedObject}
            onSelectObject={selectObject}
            onReanalyze={handleReanalyze}
            onRetag={handleRetag}
            onRestitch={handleRestitch}
            actionKey={actionKey}
          />
        )}
      </Grid>

      {detailContent}
    </VStack>
  );
};

export default ThreadManagement;
